import React, { useState, useEffect, useCallback, useRef } from 'react';
import { websocketVirtualAgent } from 'components/virtualagentws/websocketVirtualAgent';
import { useHVAConversation } from './HVARealEstateConversationContext';
import { PROPERTIES, FeedbackMessage } from './HVARealEstateData';
import { Col, Row } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  getVirtualAgentWsUrl,
  getAuthenticatedWsUrl
} from 'services/virtualagent.service';
import {
  faMicrophone,
  faMicrophoneSlash,
  faPhoneSlash,
  faPhoneVolume
} from '@fortawesome/free-solid-svg-icons';

interface ConversationMessage {
  source: 'user' | 'ai';
  message: string;
}

type HVAConversationWidgetProps = {
  agentId: string;
  agentName: string;
  onError?: (error: Error) => void;
  onFeedback: (message: Omit<FeedbackMessage, 'id' | 'timestamp'>) => void;
  firstMessage: string;
  prompt: string;
  voice: string;
  language: string;
};

const HVARealEstateWidget = ({
  agentId,
  agentName,
  onError,
  onFeedback,
  firstMessage,
  prompt,
  voice,
  language
}: HVAConversationWidgetProps): JSX.Element => {
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);

  // Primero añadimos la función de limpieza de NIF
  const cleanNIF = (nif: string): string => {
    return nif.replace(/[-.\s]/g, '').toUpperCase();
  };

  // Obtenemos el contexto de la conversación
  // eslint-disable-next-line
  const { clientInfo, setIsNifValidated, setRequestTransfer } = useHVAConversation();

  // Referencia para el contenedor de mensajes
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Función para hacer scroll hasta el último mensaje
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      const scrollContainer = messagesContainerRef.current;
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  };

  // Para adaptarnos a las instrucciones del agente
  const processFirstMessage = (
    message: string,
    agentName: string,
    customerName: string
  ): string => {
    const getSalutation = () => {
      const hour = new Date().getHours();
      if (hour < 13) return 'Buenos días';
      if (hour < 20) return 'Buenas tardes';
      return 'Buenas noches';
    };

    return message
      .replace('{{salutationDayTime}}', getSalutation())
      .replace('{{agentName}}', agentName)
      .replace('{{customerName}}', customerName);
  };

  const conversation = websocketVirtualAgent({
    onConnect: () => {
      console.log('Connected to agent');
      setIsInitialized(true);
      setIsLoading(false);
    },
    onDisconnect: () => {
      console.log('Disconnected from agent');
      setIsInitialized(false);
      setIsLoading(false);
    },
    // eslint-disable-next-line
    onError: (message: string, context?: any) => {
      console.error('Conversation error:', message, context);
      setIsLoading(false);
      if (onError) {
        onError(new Error(message));
      }
    },
    onMessage: message => {
      setMessages(prev => [
        ...prev,
        {
          source: message.source,
          message: message.message
        }
      ]);
      setTimeout(scrollToBottom, 0);
    },
    // Añadimos la configuración de client tools
    clientTools: {
      validateNIF: async (parameters: { nif: string }) => {
        // Limpiamos el NIF recibido del agente
        const cleanedNIF = cleanNIF(parameters.nif);

        // Limpiamos el NIF del formulario para comparar
        const formNIF = clientInfo?.nif ? cleanNIF(clientInfo.nif) : '';

        // Validamos que coincida con el NIF del formulario
        const isValid = formNIF === cleanedNIF;
        setIsNifValidated(isValid);

        // Añadimos mensaje de feedback
        onFeedback({
          type: 'nif',
          message: 'Solicitud de verificación de NIF',
          // eslint-disable-next-line
          details: `NIF solicitado: ${parameters.nif} Resultado: ${isValid ? '✓ NIF válido' : '✗ NIF no coincide'}`,
          success: isValid
        });

        // devolvemos la información al agente
        return JSON.stringify({
          success: isValid,
          message: isValid ? 'NIF válido' : 'NIF no encontrado en el sistema',
          validatedNif: isValid ? cleanedNIF : null
        });
      },

      requestPropertyInfo: async (parameters: { propertyId: string }) => {
        const property = PROPERTIES[parameters.propertyId];

        // Añadimos mensaje de feedback
        onFeedback({
          type: 'property',
          message: 'Solicitud de información de inmueble',
          // eslint-disable-next-line
          details: `ID Inmueble: ${parameters.propertyId} Resultado: ${property ? '✓ Inmueble encontrado' : '✗ Inmueble no encontrado'}`,
          success: !!property
        });

        return JSON.stringify({
          success: !!property,
          data: property || null,
          message: property ? null : 'Propiedad no encontrada'
        });
      },

      requestTransfer: async (parameters: { reason: string }) => {
        setRequestTransfer(true);

        // Añadimos mensaje de feedback
        onFeedback({
          type: 'transfer',
          message: 'Solicitud de transferencia',
          details: `Motivo: ${parameters.reason}`,
          success: true
        });

        return JSON.stringify({
          success: true,
          message: `Transferencia solicitada: ${parameters.reason}`,
          transferRequested: true
        });
      },

      END_CALL: async (parameters: { reason: string }) => {
        // Añadimos mensaje de feedback
        onFeedback({
          type: 'close',
          message: 'Finalización de llamada por el agente',
          details: `Motivo: ${parameters.reason}`,
          success: true
        });

        return JSON.stringify({
          success: true,
          message: `Finalización de llamada`
        });
      }
    }
  });

  const requestMicPermission = async (): Promise<boolean> => {
    try {
      await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      setHasPermission(true);
      return true;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      setHasPermission(false);
      if (onError) {
        onError(new Error('Microphone permission denied'));
      }
      return false;
    }
  };

  const startConversation = useCallback(async (): Promise<void> => {
    if (!hasPermission) {
      const permitted = await requestMicPermission();
      if (!permitted) return;
    }

    try {
      setIsLoading(true);
      setIsNifValidated(false);
      await conversation.endSession();

      // Obtener URL backend
      let signedUrl;
      if (import.meta.env.VITE_NODE_ENV === 'production') {
        // En producción, usar la función asíncrona
        signedUrl = await getAuthenticatedWsUrl();
      } else {
        // En desarrollo, usar la URL directa
        signedUrl = getVirtualAgentWsUrl();
      }

      // Iniciar sesión con URL firmada
      setMessages([]);
      await conversation.startSession({
        signedUrl,
        overrides: {
          agent: {
            prompt: {
              prompt: prompt
            },
            firstMessage: processFirstMessage(
              firstMessage,
              agentName,
              clientInfo?.name || ''
            ),
            language: language,
            tools: [
              {
                id: 'validateNIF',
                name: 'validateNIF',
                description:
                  'Valida el NIF (Número de Identificación Fiscal) del cliente',
                parameters: {
                  type: 'object',
                  properties: {
                    nif: {
                      type: 'string',
                      description:
                        'Proporciona el NIF del cliente para su validación incluso si viene con espacios, guiones o puntos'
                    }
                  },
                  required: ['nif']
                }
              },
              {
                id: 'requestPropertyInfo',
                name: 'requestPropertyInfo',
                description:
                  'Solicita información detallada sobre una propiedad inmobiliaria',
                parameters: {
                  type: 'object',
                  properties: {
                    propertyId: {
                      type: 'string',
                      description:
                        'Proporciona el ID de la propiedad para obtener información detallada sin espacios, guiones o puntos'
                    }
                  },
                  required: ['propertyId']
                }
              },
              {
                id: 'requestTransfer',
                name: 'requestTransfer',
                description:
                  'Solicita transferir la conversación a un agente humano o departamento especializado',
                parameters: {
                  type: 'object',
                  properties: {
                    reason: {
                      type: 'string',
                      description:
                        'El motivo por el que se solicita la transferencia'
                    }
                  },
                  required: ['reason']
                }
              },
              {
                id: 'END_CALL',
                name: 'END_CALL',
                description: 'Ends current conversation',
                parameters: {
                  type: 'object',
                  properties: {
                    reason: {
                      type: 'string',
                      description: 'The reason for ending the call'
                    }
                  },
                  required: []
                }
              }
            ]
          },
          tts: {
            voiceId: voice
          }
        }
      });
    } catch (error) {
      console.error('Failed to start conversation:', error);
      if (error instanceof Error && onError) {
        onError(error);
      }
    }
  }, [agentId, conversation, hasPermission, onError]);

  const endConversation = useCallback(async (): Promise<void> => {
    try {
      await conversation.endSession();
    } catch (error) {
      console.error('Failed to end conversation:', error);
      if (error instanceof Error && onError) {
        onError(error);
      }
    }
  }, [conversation, onError]);

  // Efecto para inicializar el scroll cuando se montan los mensajes
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Efecto para acceder al micrófono
  useEffect(() => {
    void requestMicPermission();
    return () => {
      if (isInitialized) {
        void endConversation();
      }
    };
  }, []);

  return (
    <div className="flex flex-col items-center p-0">
      <Row className="mb-2 mt-2">
        <Col className="pe-0" style={{ maxWidth: '170px', width: '170px' }}>
          <div className="d-flex bg-primary rounded-5 w-100 justify-content-center">
            {!isInitialized ? (
              <Button
                onClick={() => void startConversation()}
                className="p-2 text-decoration-none text-white"
                variant="link"
                disabled={!hasPermission || isLoading}
              >
                <FontAwesomeIcon icon={faPhoneVolume} className="me-2 fs-8" />
                {isLoading ? 'Connecting...' : 'Start call'}
              </Button>
            ) : (
              <Button
                onClick={() => void endConversation()}
                className="p-2 text-decoration-none text-white"
                variant="link"
              >
                <FontAwesomeIcon icon={faPhoneSlash} className="me-2 fs-8" />
                End call
              </Button>
            )}
          </div>
        </Col>
        <Col className="d-flex justify-content-end">
          <div className="d-flex justify-content-end text-end fs-10 me-2">
            <Row className="text-body-secondary">
              <div className="pt-1">
                Connected:
                {conversation.status === 'connected' ? (
                  <FontAwesomeIcon
                    icon={faPhoneVolume}
                    className="mx-2 me-0 fs-10 text-success"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faPhoneSlash}
                    className="mx-2 me-0 fs-10 text-danger"
                  />
                )}
              </div>
              <div>
                {conversation.status === 'connected' ? (
                  <>
                    Status:
                    {conversation.isSpeaking ? (
                      <FontAwesomeIcon
                        icon={faMicrophone}
                        className="mx-2 me-1 fs-10 text-success"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faMicrophoneSlash}
                        className="mx-1 me-0 fs-10 text-danger"
                      />
                    )}
                  </>
                ) : null}
              </div>
            </Row>
          </div>
        </Col>
      </Row>

      <div
        ref={messagesContainerRef}
        className="p-3 pt-2 pb-2 border rounded scrollbar bg-body-emphasis fs-9 lh-sm shadow-sm"
        style={{
          height: '350px',
          overflowY: 'auto',
          scrollBehavior: 'smooth'
        }}
      >
        {messages.length > 0 && (
          <div className="w-full space-y-2">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`p-2 rounded mb-2 ${
                  msg.source === 'user' ? 'bg-body' : 'bg-body-emphasis'
                }`}
              >
                <strong>{msg.source === 'user' ? 'Customer' : 'Agent'}:</strong>
                &nbsp;
                {msg.message}
              </div>
            ))}
          </div>
        )}
      </div>
      {!hasPermission && (
        <div className="text-sm text-yellow-600 bg-yellow-50 p-2 rounded-lg mt-2">
          Microphone permission is required for conversation
        </div>
      )}
    </div>
  );
};

export default HVARealEstateWidget;
